/**
 * ABWIN Scratch Popup — Scratch Engine
 * ---------------------------------------------------------------
 * Self-contained canvas scratch-off. Knows nothing about popup state,
 * email, or discounts — it just erases a foil layer in response to
 * pointer input and reports progress/completion via callbacks.
 */
(function (global) {
  "use strict";

  function prefersReducedMotion() {
    return (
      global.matchMedia &&
      global.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  class AbwinScratchEngine {
    constructor(canvas, options) {
      this.canvas = canvas;
      this.ctx = canvas.getContext("2d", { willReadFrequently: true });
      this.options = Object.assign(
        {
          threshold: 0.65,
          brushRadius: 22,
          sampleIntervalMs: 120,
          foilColors: ["#5a1f82", "#451166", "#33104c"],
          foilLabel: "SCRATCH TO REVEAL",
          onStart: () => {},
          onProgress: () => {},
          onThresholdReached: () => {},
        },
        options || {}
      );

      this._isDrawing = false;
      this._hasStarted = false;
      this._isComplete = false;
      this._lastSampleAt = 0;
      this._dpr = Math.min(global.devicePixelRatio || 1, 2);

      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
    }

    init() {
      this._resize();
      this._paintFoil();
      this._bindEvents();
    }

    _resize() {
      const rect = this.canvas.getBoundingClientRect();
      this.canvas.width = Math.max(1, Math.round(rect.width * this._dpr));
      this.canvas.height = Math.max(1, Math.round(rect.height * this._dpr));
      this.ctx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);
      this._cssWidth = rect.width;
      this._cssHeight = rect.height;
    }

    _paintFoil() {
      const { ctx, _cssWidth: w, _cssHeight: h, options } = this;
      const gradient = ctx.createLinearGradient(0, 0, w, h);
      gradient.addColorStop(0, options.foilColors[0]);
      gradient.addColorStop(0.5, options.foilColors[1]);
      gradient.addColorStop(1, options.foilColors[2]);
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);

      ctx.save();
      ctx.globalAlpha = 0.08;
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "700 14px Poppins, sans-serif";
      ctx.translate(0, 0);
      ctx.rotate((-18 * Math.PI) / 180);
      for (let y = -h; y < h * 2; y += 26) {
        for (let x = -w; x < w * 2; x += 130) {
          ctx.fillText("ABWIN", x, y);
        }
      }
      ctx.restore();

      ctx.fillStyle = "#FFFFFF";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "700 15px Poppins, sans-serif";
      ctx.globalAlpha = 0.95;
      ctx.fillText(options.foilLabel, w / 2, h / 2 + 2);
    }

    _bindEvents() {
      this.canvas.addEventListener("pointerdown", this._onPointerDown);
      global.addEventListener("pointermove", this._onPointerMove);
      global.addEventListener("pointerup", this._onPointerUp);
      global.addEventListener("pointercancel", this._onPointerUp);
    }

    _unbindEvents() {
      this.canvas.removeEventListener("pointerdown", this._onPointerDown);
      global.removeEventListener("pointermove", this._onPointerMove);
      global.removeEventListener("pointerup", this._onPointerUp);
      global.removeEventListener("pointercancel", this._onPointerUp);
    }

    _onPointerDown(e) {
      if (this._isComplete) return;
      this._isDrawing = true;
      if (!this._hasStarted) {
        this._hasStarted = true;
        this.options.onStart();
      }
      this._scratchAt(e);
    }

    _onPointerMove(e) {
      if (!this._isDrawing || this._isComplete) return;
      this._scratchAt(e);
    }

    _onPointerUp() {
      this._isDrawing = false;
    }

    _scratchAt(e) {
      const rect = this.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const r = this.options.brushRadius;

      this.ctx.globalCompositeOperation = "destination-out";
      this.ctx.beginPath();
      this.ctx.arc(x, y, r, 0, Math.PI * 2);
      this.ctx.fill();

      const now = performance.now();
      if (now - this._lastSampleAt > this.options.sampleIntervalMs) {
        this._lastSampleAt = now;
        this._checkProgress();
      }
    }

    _checkProgress() {
      const sampleSize = 48;
      const off = document.createElement("canvas");
      off.width = sampleSize;
      off.height = sampleSize;
      const offCtx = off.getContext("2d");
      offCtx.drawImage(this.canvas, 0, 0, sampleSize, sampleSize);

      let transparent = 0;
      const { data } = offCtx.getImageData(0, 0, sampleSize, sampleSize);
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 32) transparent++;
      }
      const fraction = transparent / (sampleSize * sampleSize);
      this.options.onProgress(fraction);

      if (fraction >= this.options.threshold && !this._isComplete) {
        this._complete();
      }
    }

    _complete() {
      this._isComplete = true;
      const finish = () => {
        this.ctx.clearRect(0, 0, this._cssWidth, this._cssHeight);
        this.options.onThresholdReached();
      };

      if (prefersReducedMotion()) {
        finish();
        return;
      }

      let progress = 0;
      const step = () => {
        progress += 0.12;
        this.ctx.globalCompositeOperation = "destination-out";
        this.ctx.globalAlpha = progress;
        this.ctx.fillRect(0, 0, this._cssWidth, this._cssHeight);
        this.ctx.globalAlpha = 1;
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          finish();
        }
      };
      requestAnimationFrame(step);
    }

    revealAll() {
      if (this._isComplete) return;
      if (!this._hasStarted) {
        this._hasStarted = true;
        this.options.onStart();
      }
      this._complete();
    }

    destroy() {
      this._unbindEvents();
    }
  }

  global.AbwinScratchEngine = AbwinScratchEngine;
})(window);
